// One loader shape for every bid screen: registered against every table the
// bid views read (TCSD-0248), `loading` on the first load only (TCSD-0039), the
// error said in words. `pollMs` re-reads on a timer — the review screen uses
// it so the BDD and the executive on a call see the same numbers: the Realtime
// publication carries no tables in this project, so a subscription would never
// fire (measured 2026-09-25).

import { useCallback, useEffect, useState } from 'react';
import { useReader } from '../../shared/save-path';
import { errorMessage } from '../../shared/errors';
import { READS } from './api';

export function useBidLoad<T>(load: () => Promise<T>, deps: unknown[], initial: T, pollMs?: number) {
  const [data, setData] = useState<T>(initial);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick((n) => n + 1), []);
  useReader(READS, refetch);

  useEffect(() => {
    if (!pollMs) return;
    const t = window.setInterval(refetch, pollMs);
    return () => window.clearInterval(t);
  }, [pollMs, refetch]);

  useEffect(() => {
    let cancelled = false;
    load()
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('[bids] load failed', e);
        setError(errorMessage((e as { cause?: unknown }).cause ?? e, (e as Error).message || 'Could not load the bid.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  return { data, loading, error, refetch };
}
